import type * as THREE from 'three';
import type { RoomDiagnostics } from '@/game/roomDiagnostics';
import type { RoomId } from '@/story/beats';
import type { Interactable } from '@/systems/interaction';
import type { HallSolid } from '@/world/solids';
import type { Framing } from '@/camera/framing';
import type { ZoneLightingOptions } from '@/render/lighting';
import type { ZonePalette } from '@/render/palette';
import type { AmbienceSource } from '@/audio/gameAudio';
import type { CarriedPose, SpawnPoint } from '@/physics/character';
import type { Pose } from '@/anim/types';
import type { CoverVolume, LightGrid, StealthSnapshot, Watcher } from '@/systems/stealth';
import type { SurfaceKind as FoleySurface } from '@/audio/sounds/foley';
import type { CullCorridorParts } from '@/world/cullCorridor';
import type { BornBay } from '@/world/bornBay';

export interface RoomTick {
  readonly dt: number;
  /** Borrowed for this call only; the host reuses it on the next fixed step. */
  readonly player: { readonly x: number; readonly y: number; readonly z: number };
  readonly raiseFlag: (flag: string) => void;
  readonly completed: (interactableId: string) => boolean;
  /**
   * True when she nudged this interactable on THIS tick. A nudge is an event,
   * not a hold, so `completed` never says it happened (beat 8's fuse is the
   * precedent, handled in `main.ts`; a module gets the same edge here).
   */
  readonly nudged: (interactableId: string) => boolean;
  /** `F` was pressed on this tick. An edge, like `nudged`; the room decides what it means. */
  readonly called: boolean;
}
/** Opaque outside the host: only the capability that created a collider may remove it. */
export interface ColliderHandle { readonly __roomCollider: unique symbol; }
export interface RoomCapabilities {
  readonly scene: { add(object: THREE.Object3D): void; remove(object: THREE.Object3D): void };
  /**
   * `add` honours the solid's authored `camera` field: a `'passes'` solid is
   * solid to her and invisible to the camera sweep (the farrowing pen rail,
   * `SWEEP_GROUPS` in `main.ts`). `addHull` is a convex hull of the given
   * corners, for a slope whose mesh is built from the same points.
   */
  readonly colliders: {
    add(solid: HallSolid): ColliderHandle;
    addHull(points: Float32Array): ColliderHandle;
    remove(handle: ColliderHandle): void;
  };
  readonly playOneShot: (id: string, options: { gain: number; position: { x: number; y: number; z: number }; detune?: number }) => void;
  /** Simulation-side sound for stealth; inert when the room has no watcher. */
  readonly emitSound: (sound: { position: { x: number; y: number; z: number }; radius: number }) => void;
  /**
   * Scale one named fixture of the room's own rig against its authored
   * intensity (0 = off, 1 = as lit). Presentation: the light count never
   * changes, so no material recompiles (the blackout hall's lesson, D-046).
   * Unknown names throw — a switch wired to nothing is the failure to catch.
   */
  readonly lights: { scale(name: string, factor: number): void };
  /** Stop the room's ambience loops now, rather than on the engine's walk-out fade. */
  readonly cutAmbience: () => void;
  readonly interaction: { setEnabled(id: string, enabled: boolean): void };
  /**
   * A fade to black the room owns, for capture. Starts clear. The same DOM
   * overlay every beat's fade is; a fake in the sim lane.
   */
  readonly createFade: () => RoomFade;
  /**
   * Beat 7's bloom, handed to a module: one interpolation between two zones
   * through lighting, fog, the grade's ceiling, exposure and the sky's
   * intensity. Beat 14 runs it the other way (the drain). Call once.
   */
  readonly createZoneBlend: (from: ZonePalette, to: ZonePalette, options: ZoneBlendOptions) => ZoneBlend;
  /**
   * Her rig's snout pivot, for a prop she carries (beat 14's bell: *"one
   * parent transform"*, D-095). Late-bound — her rig is built after the room —
   * so read it on the render step, never at construction.
   */
  readonly her: { readonly snout: THREE.Object3D };
  /**
   * The point lights of the room's own rig, in the order the rig built them —
   * so a room can hand a fixture to something that dims it (beat 1's heat
   * lamp, which the worker's lean blocks). Find one by where it is, not by
   * index: the rig builds a light per batten SEGMENT, so an index is right
   * until `BATTEN_SEGMENTS` changes and then silently wrong.
   */
  readonly rigPointLights: readonly THREE.PointLight[];
}

export type { CarriedPose };

/** A zone-to-zone interpolation the host drives through every grade reader (`Bloom`). */
export interface ZoneBlend {
  /** Raw progress 0..1, monotonic (the bloom never runs backwards). Render step only. */
  apply(t: number): void;
  /** What the grade actually holds, read back from the sinks. */
  readonly chromaCeiling: number;
}

export interface ZoneBlendOptions {
  readonly fogScale: number;
  readonly exposureFrom: number;
  readonly exposureTo: number;
  readonly skyFrom: number;
  readonly skyTo: number;
}

export interface RoomFade {
  readonly value: number;
  setTarget(value: number): void;
  update(frameDt: number): void;
  dispose(): void;
}

/**
 * A room that is watched. The host builds the `StealthSystem` from this —
 * with its own Rapier line-of-sight query, which a module cannot make — steps
 * it after the physics step, emits her footfalls into it per stride on
 * `footfallSurface`, and hands every snapshot back to `afterStep`, which is
 * where a room decides what being seen costs (D-084: capture).
 *
 * `grid` and `watchers` are the room's LIVE objects: the host holds these
 * exact references, so the room swaps what its grid samples and turns its
 * watchers in place, never by replacing them.
 */
export interface RoomStealth {
  readonly grid: LightGrid;
  readonly cover: readonly CoverVolume[];
  readonly watchers: readonly Watcher[];
  readonly footfallSurface: FoleySurface;
  afterStep(snapshot: StealthSnapshot, moveSelf: (to: SpawnPoint) => void): void;
}
/**
 * The typed handles a room offers the beat parts played in it, one optional
 * field per room that has something a part may touch. A part reaching into its
 * room is declared here, per room, rather than handed a generic bag of the
 * room's objects. Present only when the room and its state build the thing, so
 * a part that needs one checks at `create` and throws before building anything.
 *
 * The host passes these through to `BeatPartWorld.room` by `roomSeamsOf`, which
 * copies exactly these fields: a part gets the handles, not the room.
 */
export interface RoomSeams {
  /**
   * The hedgerow's breakable bay, which the ROOM builds and a beat part breaks
   * (beat 7 by the roll, beat 10 at its first frame, D-223). The host hands it
   * to the part through `BeatPartWorld.room` and holds nothing of it itself. Present only
   * in the two hedgerow rooms; a part that needs it throws at `create` without it.
   */
  readonly hedgerowBoards?: HedgerowBoardsSeam;
  /**
   * Beat 5's handles into the cull corridor it built — the sheeting the
   * machine lifts and the bin that carries her out. Present only in Act 1's
   * corridor; beat 8's return builds neither, and `CullDirector` (legacy
   * wiring in `main.ts`) is not constructed without them.
   */
  readonly cullCorridor?: CullCorridorParts;
  /**
   * Otto, as the fattening hall built him: beat 3's director resolves his rig
   * out of this group by name (`findOttoParts` throws on a rename). Present
   * only in Act 1's hall; beat 8's does not build him.
   */
  readonly otto?: THREE.Group;
  /** Beats 1 and 2's handles into the farrowing hall; see `FarrowingSeam`. */
  readonly farrowing?: FarrowingSeam;
  /** Beat 8 part 2's handles into the blackout hall; see `BlackoutHallSeam`. */
  readonly blackoutHall?: BlackoutHallSeam;
}

/** Exactly the seam fields of a room, for `BeatPartWorld.room`. Empty for no room. */
export function roomSeamsOf(room: RoomSeams | null): RoomSeams {
  if (room === null) return {};
  return {
    ...(room.hedgerowBoards === undefined ? {} : { hedgerowBoards: room.hedgerowBoards }),
    ...(room.cullCorridor === undefined ? {} : { cullCorridor: room.cullCorridor }),
    ...(room.otto === undefined ? {} : { otto: room.otto }),
    ...(room.farrowing === undefined ? {} : { farrowing: room.farrowing }),
    ...(room.blackoutHall === undefined ? {} : { blackoutHall: room.blackoutHall }),
  };
}

/**
 * What beat 8 part 2 takes from the fattening hall in its blackout state: the
 * fuse box it throws (the asset keeps its lid and lever pivots) and the hall's
 * point lights, which the fuse takes down and one of which Kestner carries in.
 * The lights are the room rig's, in build order — the order decides which
 * fitting becomes his torch (`BlackoutHallDirector.#torchLamp`).
 */
export interface BlackoutHallSeam {
  readonly fuseBox: THREE.Object3D;
  readonly lights: readonly THREE.PointLight[];
}

/**
 * What the render step can see, for a room whose presentation follows the lens
 * (the farrowing hall's batten cull). Borrowed for the call; the host reuses it.
 * Read after the camera has settled for the frame, so nothing lags the image.
 */
export interface RoomView {
  readonly camera: { readonly x: number; readonly y: number; readonly z: number };
}

/**
 * What she is standing on: which footfall the audio plays, and whether she is
 * in a pen, which takes the camera's `pen` framing over every gait (R-014).
 * One answer for both on purpose — a camera that thinks she is in the pen while
 * the footfalls think she is on concrete is two systems disagreeing about where
 * she stands. Return shared constants: it is asked every frame.
 */
export interface Underfoot {
  readonly surface: FoleySurface;
  readonly pen: boolean;
}

export interface RoomModule extends RoomSeams {
  readonly interactables: readonly Interactable[];
  fixedStep(tick: RoomTick): void;
  renderStep(frameDt: number, view: RoomView): void;
  /**
   * Omit for a room with one floor and no pen: the host then leaves the
   * footfall surface at its default and never picks the pen framing.
   * Presentation only — read on the render step.
   */
  readonly underfoot?: (x: number, z: number) => Underfoot;
  framing(observingId: string | null): Framing | null;
  diagnostics(): RoomDiagnostics;
  /**
   * Her body, while the room has it: non-null means the host puts her HERE
   * after the room's fixed step, every tick, and her own movement that tick is
   * discarded. That is how a shot carries her (D-095) and how controls are
   * withdrawn without a second flag — beat 5's bin and beat 6's trailer do
   * the same with `character.reset` in their legacy branches. Read on the
   * fixed step. Beat 13's surge is the first module to use it.
   */
  readonly carry?: CarriedPose | null;
  /**
   * An authored delta on her pose, applied on the render step after perception
   * and before the rig: beat 14's head dip to the bell and the carry's hold.
   * Deltas only, like every pose (`src/anim/types.ts`). Optional.
   */
  posePatch?(pose: Pose): void;
  /** The beat's cues, forwarded after the beat part has seen them. Optional. */
  handleCue?(cue: { id: string }): void;
  /** Present when the room is watched; see `RoomStealth`. */
  readonly stealth?: RoomStealth;
  /**
   * What `F` would do right now, for the host's prompt and touch button: the
   * verb, or null when pressing it would do nothing. Omit for a room with no
   * companion. Read on the render step; it must not change simulation.
   */
  readonly callVerb?: 'call' | null;
  dispose(): void;
}

/**
 * What beats 1 and 2 take from the farrowing hall the room built: her bay and
 * the two colliders a beat removes — the taken sibling's (beat 1's cue, beat 2 at
 * boot) and the shut pen gate's (beat 2 swaps it for one that turns).
 */
export interface FarrowingSeam {
  readonly bay: BornBay;
  /**
   * The heat lamp's intensity as the rig built it, read before anything dims
   * it: `beatDebug.lampLevel` is a ratio against the value the ZONE chose,
   * and reading it back after the director has touched it would make the
   * ratio 1.0 for ever. 0 when the rig has no lamp.
   */
  readonly lampFullIntensity: number;
  /** Idempotent: a second call does nothing. */
  removeTakenSiblingCollider(): void;
  /** Idempotent: a second call does nothing. */
  removeShutGateCollider(): void;
  /**
   * The walkway door's `door-hinge`: the hall builds the door, the beats
   * swing it. Turning it moves the picture only; the collider is below.
   */
  readonly walkwayDoorHinge: THREE.Object3D;
  /** The hall's SHUT door leaf collider. Idempotent: a second call does nothing. */
  removeWalkwayDoorCollider(): void;
}

/**
 * Beat 7's breakable bay, which the ROOM built and a part breaks. A part
 * reaching into its room is declared per room, here, rather than handed a
 * generic bag of the room's objects: the part names exactly the two things it
 * may touch, and a room that does not have them does not offer this.
 */
export interface HedgerowBoardsSeam {
  /** The intact bay as the room built it: the shudder moves it, the break removes it. */
  readonly boards: THREE.Object3D;
  /** Removes exactly the bay's collider. Idempotent: a second call does nothing. */
  removeBoardsCollider(): void;
}
/**
 * What `scene.background` holds. `rig` is the lighting rig's flat zone colour;
 * `sky` bakes `createSky(zone, { seed, zenith, middle })`, whose texture the host owns and
 * disposes on context loss; a colour is used as given.
 *
 * `sky.zone` bakes from a palette other than the room's own. Beat 7's field is
 * lit in the yard's zone and blooms into `outside`, and its sky is baked from
 * where the bloom ENDS, then revealed by `backgroundIntensity` (D-106: one
 * texture, no cross-fade). Absent means the room's zone.
 */
export type RoomBackground =
  | { readonly kind: 'rig' }
  | { readonly kind: 'sky'; readonly seed: number; readonly zenith?: number; readonly middle?: number; readonly zone?: ZonePalette }
  | THREE.Color;

/**
 * Everything the host needs to light and populate a room before the module
 * exists: its palette, its rig, its sky, where she starts and what it sounds
 * like. Declared by the room, applied by one generic branch in `main.ts`, so a
 * new room is a module and not another fifty-line `else if` there (pathfinder
 * rule, session 66).
 */
export interface RoomEnvironment {
  readonly zone: ZonePalette;
  readonly lighting: ZoneLightingOptions;
  readonly background: RoomBackground;
  readonly spawn: SpawnPoint;
  readonly ambience: readonly AmbienceSource[];
  /**
   * Rain density for the post stack, constant for the room (beat 6's yard and
   * beat 7's field). Absent or 0 means no weather: the host only advances rain
   * in a room that declared it, which is the flag the hedgerow's frozen rain
   * taught (see `weatherActive` in `main.ts`).
   */
  readonly rain?: number;
}

export interface RoomModuleDefinition {
  readonly room: RoomId;
  /**
   * The ledger sheet her notes go on, when it is not this room's id. Only for
   * a room that shares another's origin and metres — the same place at another
   * hour — or her map would draw one room's walk over another's floor plan.
   */
  readonly ledgerSheet?: RoomId;
  environment(options: RoomOptions): RoomEnvironment;
  create(options: RoomCreateOptions, capabilities: RoomCapabilities): RoomModule;
}

export interface RoomOptions {
  readonly part: number;
  /**
   * The beat booting the room, for a room whose state depends on which beat
   * returns to it: beat 8 walks back through the cull corridor and the
   * fattening hall as they are after Act 1 (`returnState`). `part` alone
   * cannot say so — beat 5's corridor and beat 8's are both part 1. Absent
   * means the room as its first beat built it; the sim lane leaves it out.
   */
  readonly beat?: number;
}

export interface RoomCreateOptions extends RoomOptions {
  /** `?dress=0`: the room without its dressing, the budget control. */
  readonly dressing: boolean;
  readonly out?: number;
  /**
   * `?battens=N`: how many ceiling battens stay lit, nearest the camera first
   * (experiment S1, D-046). Absent means all of them, which is what ships.
   * Only the farrowing hall has battens to cull; every other room ignores it.
   */
  readonly battens?: number;
}
